import { AHText, AHWidgetTitle, Box, TouchableBox } from "arkh";
import React, { useCallback, useMemo } from "react";

import { DateTime } from "luxon";
import { GraphCheck } from "./GraphCheck";
import { GraphContainer } from "../Graph/Graph";
import { SingleTaskInProgressCheck } from "../SingleTaskInProgress";

import { useCombineCheckTaskExecutions } from "../taskExecutionList";

import { SingleSectionTitle } from "../SingleSectionTitle";
import { graphql, useFragment } from "react-relay";
import { WidgetTaskSingleCheck_taskCheck$key } from "./__generated__/WidgetTaskSingleCheck_taskCheck.graphql";

import { WidgetTaskSingleCheckSmallInProgress_taskExecutions$key } from "./__generated__/WidgetTaskSingleCheckSmallInProgress_taskExecutions.graphql";
import {
  TaskExecutionCheckSentimentIcon,
  TaskExecutionCheckSentimentNoAnswerIcon,
} from "../../components/TaskExecutionCheckSentimentIcon";
import type { Section } from "../useWidgetTaskSingleSections";
import { WidgetTaskSingleCheck_taskCheckOccasion$key } from "./__generated__/WidgetTaskSingleCheck_taskCheckOccasion.graphql";

export function WidgetTaskSingleCheck({
  task: _task,
  large,
  onPress,
  sections,
  maxOccasions,
  maxParts,
}: {
  task: WidgetTaskSingleCheck_taskCheck$key;
  large?: boolean;
  onPress: (sequenceNumbers: number[]) => void;
  sections: Section[];
  maxOccasions?: number;
  maxParts?: number;
}) {
  const task = useFragment(
    graphql`
      fragment WidgetTaskSingleCheck_taskCheck on TaskCheck {
        id
        title
        routine {
          id
          title
        }
        occasions(first: 8) {
          edges {
            node {
              id
              ...WidgetTaskSingleCheck_taskCheckOccasion
              taskExecutions {
                id
                checkValue
                sectionId
                ...WidgetTaskSingleCheckSmallInProgress_taskExecutions
                ...SingleTaskInProgressCheck_taskExecutionCheck
              }
              occasion {
                id
                startAt
                endAt
                sequenceNumber
              }
            }
          }
        }
      }
    `,
    _task
  );

  const inProgressOccasion = task.occasions.edges[0]?.node;
  const sortedSpecifiedInProgressTaskExecutions = useMemo(
    () =>
      inProgressOccasion?.taskExecutions
        .map((te) => [te, sections.find((s) => s.id === te.sectionId)] as const)
        .filter(([te, s]) => s)
        .sort(([_a, a], [_b, b]) => a!.ordinal - b!.ordinal)
        .map(([te]) => te) ?? [],
    [sections, inProgressOccasion?.taskExecutions]
  );

  const history = useMemo(() => {
    return task.occasions.edges
      .slice(0, maxOccasions ?? (large ? 8 : 5))
      .map((v) => v.node);
  }, [large, maxOccasions, task.occasions.edges]);

  const _op = useCallback(() => {
    onPress(history.map((h) => h.occasion.sequenceNumber));
  }, [history, onPress]);

  if (large) {
    return (
      <TouchableBox
        onPress={_op}
        style={{ flex: 1, flexDirection: "row" }}
        color="surface"
      >
        <Box style={{ flex: 1 }}>
          <AHWidgetTitle color="darkerText">
            {task.title}
            {sections.length === 1 ? (
              <SingleSectionTitle section={sections[0]} />
            ) : null}
          </AHWidgetTitle>
          <AHText spaceHorizontal="medium" type="detail" numberOfLines={1}>
            {task.routine.title}
          </AHText>
          <CheckGraph data={history} sections={sections} />
        </Box>
        {inProgressOccasion ? (
          <SingleTaskInProgressCheck
            taskExecutions={sortedSpecifiedInProgressTaskExecutions}
            sections={sections}
            maxParts={maxParts}
          />
        ) : null}
      </TouchableBox>
    );
  }
  return (
    <TouchableBox onPress={_op} style={{ flex: 1 }} color="surface">
      <Box
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box style={{ flex: 1 }}>
          <AHWidgetTitle color="darkerText">
            {task.title}
            {sections.length === 1 ? (
              <SingleSectionTitle section={sections[0]} />
            ) : null}
          </AHWidgetTitle>
          <AHText spaceHorizontal="medium" type="detail" numberOfLines={1}>
            {task.routine.title}
          </AHText>
        </Box>
        <SmallInProgress
          taskExecutions={sortedSpecifiedInProgressTaskExecutions}
        />
      </Box>

      <CheckGraph data={history} sections={sections} />
    </TouchableBox>
  );
}

function CheckGraph(props: {
  data: WidgetTaskSingleCheck_taskCheckOccasion$key;
  sections: Section[];
}) {
  const data = useFragment(
    graphql`
      fragment WidgetTaskSingleCheck_taskCheckOccasion on TaskCheckOccasion
      @relay(plural: true) {
        id
        occasion {
          startAt
          endAt
        }
        taskExecutions {
          sectionId
          checkValue
        }
      }
    `,
    props.data
  );
  const time = DateTime.local();
  const historyItems = useMemo(
    () =>
      data
        .map((h) => {
          const startAt = DateTime.fromISO(h.occasion.startAt);
          const endAt = DateTime.fromISO(h.occasion.endAt);
          const isActive = time > startAt && time < endAt;

          return {
            id: h.id,
            isActive,
            label: startAt.toFormat("d"),
            value: h.taskExecutions
              .map(
                (te) =>
                  [
                    te,
                    props.sections.find((s) => s.id === te.sectionId),
                  ] as const
              )
              .filter(([te, s]) => s)
              .sort(([_a, a], [_b, b]) => a!.ordinal - b!.ordinal)
              .map(([r, section]) =>
                r.checkValue === null ? null : r.checkValue === "POSITIVE"
              ),
          };
        })
        .reverse(),
    [data, props.sections, time]
  );

  return (
    <GraphContainer>
      <GraphCheck data={historyItems} />
    </GraphContainer>
  );
}

function SmallInProgress(props: {
  taskExecutions: WidgetTaskSingleCheckSmallInProgress_taskExecutions$key;
}) {
  const taskExecutions = useFragment(
    graphql`
      fragment WidgetTaskSingleCheckSmallInProgress_taskExecutions on TaskExecutionCheck
      @relay(plural: true) {
        checkValue
        ...taskExecutionListCombineCheckTaskExecutions
      }
    `,
    props.taskExecutions
  );
  const combined = useCombineCheckTaskExecutions(taskExecutions);

  const render = () => {
    if (taskExecutions.length === 1) {
      return (
        <TaskExecutionCheckSentimentIcon
          size="medium"
          sentiment={taskExecutions[0].checkValue}
        />
      );
    }
    if (combined.percentage === null) {
      <TaskExecutionCheckSentimentNoAnswerIcon size="medium" />;
    }
    return (
      <AHText
        type="title"
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.6}
      >
        {combined.percentage}%
      </AHText>
    );
  };
  return (
    <Box style={{ flexDirection: "row" }} spaceRight="medium" spaceTop="small">
      {render()}
    </Box>
  );
}
